/** @jsxImportSource @emotion/react */

import React, { ChangeEvent, FC, memo, useCallback, useMemo } from 'react'
import { useForm } from 'react-final-form'
import { toast } from 'utils/toast'
import * as classes from '../styles'
import { PeriodonticsTableData } from './types'
import {
  PERIODONTICS_TABLE_GROUPS,
  getCellKey,
  MIN_TABLE_WIDTH,
  TableGroupConfig,
  TableRowConfig,
  MAIN_COLUMN_COUNT,
} from './config'

// Lightweight native cell components for better performance (340+ cells)
type TextCellProps = {
  cellKey: string
  value: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  fieldCount: number
}

const TextCell = memo<TextCellProps>(({ cellKey, value, onChange, fieldCount }) => (
  <td key={cellKey} css={classes.cellWithGap(fieldCount)}>
    <input type="text" value={value} onChange={onChange} css={classes.nativeInput} />
  </td>
))
TextCell.displayName = 'TextCell'

type BooleanCellProps = {
  cellKey: string
  checked: boolean
  onToggle: () => void
  fieldCount: number
}

const BooleanCell = memo<BooleanCellProps>(({ cellKey, checked, onToggle, fieldCount }) => (
  <td key={cellKey} css={classes.cellWithGap(fieldCount)}>
    <div css={classes.checkboxWrapper}>
      <input type="checkbox" checked={checked} onChange={onToggle} css={classes.nativeCheckbox} />
    </div>
  </td>
))
BooleanCell.displayName = 'BooleanCell'

type Props = {
  name: string
  value?: string | null
}

/*
Expected data structure (flat, only non-empty values):
{
  "vestibular_furcation_0_0": "1", "vestibular_furcation_0_1": "2", "vestibular_furcation_0_2": "3",
  "vestibular_furcation_1_0": "4", "vestibular_furcation_1_1": "5", "vestibular_furcation_1_2": "6",
  "tooth_0_0": "4.2", "tooth_1_0": "4.1",
  "lingual_bop_0_0": "1", "lingual_bop_0_1": "1",
  ...
}
Key format: "{groupKey}_{rowKey}_{columnIndex}_{cellIndex}" or "{rowKey}_{columnIndex}_{cellIndex}" (if no group)
Only non-empty values are stored in JSON.
*/

const parseTableData = (value: string | null | undefined): PeriodonticsTableData => {
  if (!value) {
    return {}
  }

  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value
    return parsed || {}
  } catch {
    toast.error('Invalid periodontics table data')
    console.error('Invalid periodontics table data:', value)
    return {}
  }
}

export const PeriodonticsTable: FC<Props> = ({ name, value }) => {
  const form = useForm()
  const tableData = useMemo(() => parseTableData(value), [value])

  const updateData = useCallback(
    (updater: (data: PeriodonticsTableData) => PeriodonticsTableData) => {
      const updated = updater(tableData)
      // Filter out empty values before saving
      const filtered: PeriodonticsTableData = {}
      Object.entries(updated).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          filtered[key] = value
        }
      })
      form.change(name, Object.keys(filtered).length > 0 ? JSON.stringify(filtered) : '')
    },
    [form, name, tableData]
  )

  const updateCell = useCallback(
    (groupKey: string | undefined, rowKey: string, columnIndex: number, cellIndex: number, newValue: string) => {
      updateData((data) => {
        const key = getCellKey(groupKey, rowKey, columnIndex, cellIndex)
        const updated = { ...data }

        if (newValue && newValue.trim() !== '') {
          updated[key] = newValue
        } else {
          // Remove key if value is empty
          delete updated[key]
        }

        return updated
      })
    },
    [updateData]
  )

  const toggleBooleanCell = useCallback(
    (groupKey: string | undefined, rowKey: string, columnIndex: number, cellIndex: number) => {
      updateData((data) => {
        const key = getCellKey(groupKey, rowKey, columnIndex, cellIndex)
        const updated = { ...data }

        if (updated[key] === '1') {
          // Uncheck: remove from JSON
          delete updated[key]
        } else {
          // Check: set to "1"
          updated[key] = '1'
        }

        return updated
      })
    },
    [updateData]
  )

  const renderCell = useCallback(
    (group: TableGroupConfig, row: TableRowConfig, columnIndex: number, cellIndex: number) => {
      const cellKey = getCellKey(group.groupKey, row.key, columnIndex, cellIndex)
      const cellValue = tableData[cellKey] || ''

      if (row.isBooleanRow) {
        return (
          <BooleanCell
            key={cellKey}
            cellKey={cellKey}
            checked={cellValue === '1'}
            onToggle={() => toggleBooleanCell(group.groupKey, row.key, columnIndex, cellIndex)}
            fieldCount={row.fieldCount}
          />
        )
      }

      return (
        <TextCell
          key={cellKey}
          cellKey={cellKey}
          value={cellValue}
          onChange={(e) => updateCell(group.groupKey, row.key, columnIndex, cellIndex, e.target.value)}
          fieldCount={row.fieldCount}
        />
      )
    },
    [tableData, toggleBooleanCell, updateCell]
  )

  const getGroupKey = (group: TableGroupConfig) => group.groupKey || group.groupTitle || group.rows[0]?.key || 'unknown'

  const renderGroup = (group: TableGroupConfig) => (
    <div key={getGroupKey(group)} css={classes.groupWrapper}>
      {group.groupTitle && <div css={classes.groupTitle}>{group.groupTitle}</div>}
      <div css={classes.groupContainer}>
        {group.rows.map((row) => {
          // Generate cells: for each main column, render fieldCount cells
          const cells: React.ReactNode[] = []
          for (let columnIndex = 0; columnIndex < MAIN_COLUMN_COUNT; columnIndex++) {
            for (let cellIndex = 0; cellIndex < row.fieldCount; cellIndex++) {
              cells.push(renderCell(group, row, columnIndex, cellIndex))
            }
          }
          return (
            <div key={row.key} css={classes.rowContainer}>
              <div css={classes.rowTitle}>{row.title}</div>
              <table css={classes.table}>
                <tbody>
                  <tr>{cells}</tr>
                </tbody>
              </table>
            </div>
          )
        })}
      </div>
    </div>
  )

  return (
    <div css={classes.tableContainer(MIN_TABLE_WIDTH)}>
      {PERIODONTICS_TABLE_GROUPS.map((group) => renderGroup(group))}
    </div>
  )
}
